import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Calendar, Plus, Edit, Trash2, Loader2, Users, MapPin, Ticket, Clock,
  Search, QrCode, CheckCircle, XCircle, Send, Download, BarChart3, X, ChevronLeft, Camera, CameraOff
} from 'lucide-react';
import { toast } from 'sonner';

const defaultEventForm = {
  title: '', description: '', thumbnail: '', type: 'offline',
  status: 'draft', venue_name: '', venue_address: '', venue_city: '',
  venue_map_url: '', organizer_name: '', organizer_phone: '', organizer_email: '',
  start_date: '', end_date: '', category: '', tags: [], is_published: false,
};

const defaultTicketTypeForm = {
  name: '', description: '', benefits: [], price: 0, quota: 0,
  sale_start: '', sale_end: '', section_name: '', color: '#3B82F6',
  sort_order: 0, is_active: true,
};

const defaultScheduleForm = {
  date: '', day_label: '', sessions: [{ time: '', end_time: '', title: '', description: '', speaker_name: '', location: '' }],
  sort_order: 0,
};

const defaultVenueSectionForm = {
  name: '', capacity: 0, color: '#3B82F6', description: '', sort_order: 0,
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-800', upcoming: 'bg-blue-100 text-blue-800',
  ongoing: 'bg-red-100 text-red-800', completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-orange-100 text-orange-800',
};
const statusLabels = {
  draft: 'Draft', upcoming: 'Akan Datang', ongoing: 'Berlangsung',
  completed: 'Selesai', cancelled: 'Dibatalkan',
};

function formatDate(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
}
function formatPrice(n) { return `Rp ${(n || 0).toLocaleString('id-ID')}`; }

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultEventForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState('info');

  // Sub-form states
  const [showTTForm, setShowTTForm] = useState(false);
  const [ttForm, setTtForm] = useState(defaultTicketTypeForm);
  const [ttEditId, setTtEditId] = useState(null);
  const [showSchedForm, setShowSchedForm] = useState(false);
  const [schedForm, setSchedForm] = useState(defaultScheduleForm);
  const [schedEditId, setSchedEditId] = useState(null);
  const [showVSForm, setShowVSForm] = useState(false);
  const [vsForm, setVsForm] = useState(defaultVenueSectionForm);
  const [vsEditId, setVsEditId] = useState(null);

  // Registrations & tickets
  const [registrations, setRegistrations] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchReg, setSearchReg] = useState('');

  // Check-in
  const [checkInInput, setCheckInInput] = useState('');
  const [checkInResult, setCheckInResult] = useState(null);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [scannerPending, setScannerPending] = useState(false);
  const scannerRef = useRef(null);
  const scannerContainerId = 'qr-scanner-container';

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/events');
      setEvents(data.events || []);
    } catch { toast.error('Gagal memuat events'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const fetchDetail = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/tenant/events/${id}`);
      setSelected(data);
    } catch { toast.error('Gagal memuat detail event'); }
  }, []);

  const fetchRegistrations = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/tenant/events/${id}/registrations`);
      setRegistrations(data.registrations || []);
    } catch {}
  }, []);

  const fetchTickets = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/tenant/events/${id}/tickets`);
      setTickets(data.tickets || []);
    } catch {}
  }, []);

  const fetchStats = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/tenant/events/${id}/stats`);
      setStats(data);
    } catch {}
  }, []);

  const openDetail = (ev) => {
    fetchDetail(ev.id);
    fetchRegistrations(ev.id);
    fetchTickets(ev.id);
    fetchStats(ev.id);
    setDetailTab('info');
    setCheckInResult(null);
    setCheckInInput('');
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Judul event wajib diisi');
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/tenant/events/${editId}`, form);
        toast.success('Event diupdate');
      } else {
        await api.post('/tenant/events', form);
        toast.success('Event dibuat');
      }
      setShowForm(false); setForm(defaultEventForm); setEditId(null);
      fetchEvents();
      if (selected) fetchDetail(selected.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus event ini?')) return;
    try {
      await api.delete(`/tenant/events/${id}`);
      toast.success('Event dihapus');
      if (selected?.id === id) setSelected(null);
      fetchEvents();
    } catch { toast.error('Gagal menghapus'); }
  };

  const handleEdit = (ev) => {
    setForm({
      title: ev.title || '', description: ev.description || '', thumbnail: ev.thumbnail || '',
      type: ev.type || 'offline', status: ev.status || 'draft',
      venue_name: ev.venue_name || '', venue_address: ev.venue_address || '',
      venue_city: ev.venue_city || '', venue_map_url: ev.venue_map_url || '',
      organizer_name: ev.organizer_name || '', organizer_phone: ev.organizer_phone || '',
      organizer_email: ev.organizer_email || '',
      start_date: (ev.start_date || '').slice(0, 16), end_date: (ev.end_date || '').slice(0, 16),
      category: ev.category || '', tags: ev.tags || [], is_published: ev.is_published || false,
    });
    setEditId(ev.id);
    setShowForm(true);
  };

  // ---- Ticket Types ----
  const handleSaveTT = async () => {
    if (!ttForm.name.trim()) return toast.error('Nama tipe tiket wajib diisi');
    setSaving(true);
    try {
      if (ttEditId) {
        await api.put(`/tenant/events/${selected.id}/ticket-types/${ttEditId}`, ttForm);
        toast.success('Tipe tiket diupdate');
      } else {
        await api.post(`/tenant/events/${selected.id}/ticket-types`, ttForm);
        toast.success('Tipe tiket ditambahkan');
      }
      setShowTTForm(false); setTtForm(defaultTicketTypeForm); setTtEditId(null);
      fetchDetail(selected.id); fetchStats(selected.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
    setSaving(false);
  };

  const handleDeleteTT = async (ttId) => {
    if (!window.confirm('Hapus tipe tiket ini?')) return;
    try {
      await api.delete(`/tenant/events/${selected.id}/ticket-types/${ttId}`);
      toast.success('Tipe tiket dihapus');
      fetchDetail(selected.id); fetchStats(selected.id);
    } catch { toast.error('Gagal menghapus'); }
  };

  // ---- Schedules ----
  const handleSaveSched = async () => {
    if (!schedForm.day_label.trim()) return toast.error('Label hari wajib diisi');
    setSaving(true);
    try {
      if (schedEditId) {
        await api.put(`/tenant/events/${selected.id}/schedules/${schedEditId}`, schedForm);
        toast.success('Jadwal diupdate');
      } else {
        await api.post(`/tenant/events/${selected.id}/schedules`, schedForm);
        toast.success('Jadwal ditambahkan');
      }
      setShowSchedForm(false); setSchedForm(defaultScheduleForm); setSchedEditId(null);
      fetchDetail(selected.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
    setSaving(false);
  };

  const handleDeleteSched = async (sid) => {
    if (!window.confirm('Hapus jadwal ini?')) return;
    try {
      await api.delete(`/tenant/events/${selected.id}/schedules/${sid}`);
      toast.success('Jadwal dihapus');
      fetchDetail(selected.id);
    } catch { toast.error('Gagal menghapus'); }
  };

  // ---- Venue Sections ----
  const handleSaveVS = async () => {
    if (!vsForm.name.trim()) return toast.error('Nama section wajib diisi');
    setSaving(true);
    try {
      if (vsEditId) {
        await api.put(`/tenant/events/${selected.id}/venue-sections/${vsEditId}`, vsForm);
        toast.success('Venue section diupdate');
      } else {
        await api.post(`/tenant/events/${selected.id}/venue-sections`, vsForm);
        toast.success('Venue section ditambahkan');
      }
      setShowVSForm(false); setVsForm(defaultVenueSectionForm); setVsEditId(null);
      fetchDetail(selected.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
    setSaving(false);
  };

  const handleDeleteVS = async (vid) => {
    if (!window.confirm('Hapus venue section ini?')) return;
    try {
      await api.delete(`/tenant/events/${selected.id}/venue-sections/${vid}`);
      toast.success('Venue section dihapus');
      fetchDetail(selected.id);
    } catch { toast.error('Gagal menghapus'); }
  };

  // ---- Check-In ----
  const handleCheckIn = async () => {
    if (!checkInInput.trim()) return;
    setCheckInLoading(true); setCheckInResult(null);
    try {
      const payload = checkInInput.startsWith('EVT:')
        ? { qr_data: checkInInput } : { ticket_number: checkInInput };
      const { data } = await api.post(`/tenant/events/${selected.id}/check-in`, payload);
      setCheckInResult({ success: true, ...data });
      toast.success('Check-in berhasil!');
      setCheckInInput('');
      fetchTickets(selected.id);
      fetchStats(selected.id);
    } catch (e) {
      setCheckInResult({ success: false, message: e.response?.data?.detail || 'Check-in gagal' });
    }
    setCheckInLoading(false);
  };

  const handleUndoCheckIn = async (ticketId) => {
    try {
      await api.post(`/tenant/events/${selected.id}/check-in/${ticketId}/undo`);
      toast.success('Check-in dibatalkan');
      fetchTickets(selected.id); fetchStats(selected.id);
    } catch { toast.error('Gagal membatalkan check-in'); }
  };

  // ---- QR Scanner ----
  const startScanner = () => {
    setScannerPending(true);
    setScannerActive(true);
  };

  // Actually init scanner after container is rendered
  useEffect(() => {
    if (!scannerPending || !scannerActive) return;
    let cancelled = false;
    const initScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (cancelled) return;
        if (scannerRef.current) {
          try { await scannerRef.current.stop(); } catch {}
        }
        const el = document.getElementById(scannerContainerId);
        if (!el) { toast.error('Scanner container not found'); return; }
        const scanner = new Html5Qrcode(scannerContainerId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            setCheckInInput(decodedText);
            stopScanner();
            setTimeout(() => {
              const payload = decodedText.startsWith('EVT:')
                ? { qr_data: decodedText } : { ticket_number: decodedText };
              setCheckInLoading(true); setCheckInResult(null);
              api.post(`/tenant/events/${selected.id}/check-in`, payload)
                .then(({ data }) => {
                  setCheckInResult({ success: true, ...data });
                  toast.success('Check-in berhasil!');
                  setCheckInInput('');
                  fetchTickets(selected.id);
                  fetchStats(selected.id);
                })
                .catch(e => {
                  setCheckInResult({ success: false, message: e.response?.data?.detail || 'Check-in gagal' });
                })
                .finally(() => setCheckInLoading(false));
            }, 300);
          },
          () => {}
        );
      } catch (e) {
        if (!cancelled) {
          toast.error('Gagal membuka kamera: ' + (e.message || e));
          setScannerActive(false);
        }
      }
      setScannerPending(false);
    };
    // Small delay to ensure DOM is ready
    const timer = setTimeout(initScanner, 100);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [scannerPending, scannerActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch {}
      scannerRef.current = null;
    }
    setScannerActive(false);
  };

  // Cleanup scanner on unmount or tab change
  useEffect(() => {
    return () => { if (scannerRef.current) { try { scannerRef.current.stop(); } catch {} } };
  }, []);

  // ---- Send Reminder ----
  const handleSendReminder = async () => {
    if (!window.confirm('Kirim reminder ke semua peserta?')) return;
    try {
      const { data } = await api.post(`/tenant/events/${selected.id}/send-reminder`);
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal mengirim'); }
  };

  // ---- RENDER ----

  // Event List
  if (!selected) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Calendar size={24} /> Event & Ticketing</h1>
            <p className="text-sm text-slate-500 mt-1">Kelola event offline, tiket, jadwal, dan check-in peserta</p>
          </div>
          <Button onClick={() => { setForm(defaultEventForm); setEditId(null); setShowForm(true); }}>
            <Plus size={16} className="mr-2" /> Buat Event
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin mr-2" /> Memuat...</div>
        ) : events.length === 0 ? (
          <Card><CardContent className="text-center py-16 text-slate-500">
            <Calendar size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="font-medium">Belum ada event</p>
            <p className="text-sm mt-1">Klik "Buat Event" untuk membuat event pertama Anda</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {events.map(ev => (
              <Card key={ev.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDetail(ev)}>
                <CardContent className="p-0">
                  {ev.thumbnail ? (
                    <img src={ev.thumbnail} alt="" className="w-full h-40 object-cover rounded-t-lg" />
                  ) : (
                    <div className="w-full h-40 bg-gradient-to-br from-violet-500 to-purple-600 rounded-t-lg flex items-center justify-center">
                      <Calendar size={40} className="text-white/70" />
                    </div>
                  )}
                  <div className="p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge className={statusColors[ev.status] || 'bg-gray-100'}>{statusLabels[ev.status] || ev.status}</Badge>
                      {ev.is_published && <Badge className="bg-emerald-100 text-emerald-700">Published</Badge>}
                    </div>
                    <h3 className="font-bold text-slate-900 line-clamp-1">{ev.title}</h3>
                    {ev.venue_name && <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={12} />{ev.venue_name}{ev.venue_city ? `, ${ev.venue_city}` : ''}</p>}
                    {ev.start_date && <p className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12} />{formatDate(ev.start_date)}</p>}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <span className="text-xs text-slate-500 flex items-center gap-1"><Users size={12} />{ev.total_sold || 0}/{ev.total_capacity || '∞'}</span>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(ev)}><Edit size={14} /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(ev.id)}><Trash2 size={14} /></Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <EventFormDialog open={showForm} onOpenChange={setShowForm} form={form} setForm={setForm}
          editId={editId} saving={saving} onSave={handleSave} />
      </div>
    );
  }

  // Event Detail
  const ev = selected;
  const filteredRegs = registrations.filter(r =>
    !searchReg || r.customer_name?.toLowerCase().includes(searchReg.toLowerCase()) ||
    r.customer_email?.toLowerCase().includes(searchReg.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => setSelected(null)}><ChevronLeft size={18} /></Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">{ev.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={statusColors[ev.status]}>{statusLabels[ev.status] || ev.status}</Badge>
            {ev.venue_name && <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={12} />{ev.venue_name}</span>}
            {ev.start_date && <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12} />{formatDate(ev.start_date)}</span>}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => handleEdit(ev)}><Edit size={14} className="mr-1" /> Edit</Button>
        <Button variant="outline" size="sm" onClick={handleSendReminder}><Send size={14} className="mr-1" /> Kirim Reminder</Button>
      </div>

      <Tabs value={detailTab} onValueChange={setDetailTab}>
        <TabsList>
          <TabsTrigger value="info"><BarChart3 size={14} className="mr-1" />Info</TabsTrigger>
          <TabsTrigger value="tickets"><Ticket size={14} className="mr-1" />Tiket</TabsTrigger>
          <TabsTrigger value="schedule"><Calendar size={14} className="mr-1" />Jadwal</TabsTrigger>
          <TabsTrigger value="venue"><MapPin size={14} className="mr-1" />Venue</TabsTrigger>
          <TabsTrigger value="registrations"><Users size={14} className="mr-1" />Peserta</TabsTrigger>
          <TabsTrigger value="checkin"><QrCode size={14} className="mr-1" />Check-in</TabsTrigger>
        </TabsList>

        {/* INFO TAB */}
        <TabsContent value="info" className="space-y-4">
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.total_sold}</p>
                <p className="text-xs text-slate-500">Tiket Terjual</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.total_checked_in}</p>
                <p className="text-xs text-slate-500">Sudah Check-in</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-violet-600">{stats.total_capacity || '∞'}</p>
                <p className="text-xs text-slate-500">Total Kapasitas</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{formatPrice(stats.total_revenue)}</p>
                <p className="text-xs text-slate-500">Total Pendapatan</p>
              </CardContent></Card>
            </div>
          )}
          {stats?.ticket_types?.length > 0 && (
            <Card><CardHeader><CardTitle className="text-sm">Per Tipe Tiket</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.ticket_types.map(tt => (
                    <div key={tt.id} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tt.color }} />
                      <span className="text-sm font-medium flex-1">{tt.name}</span>
                      <span className="text-xs text-slate-500">{formatPrice(tt.price)}</span>
                      <span className="text-xs text-slate-500">{tt.sold}/{tt.quota || '∞'} terjual</span>
                      <span className="text-xs text-slate-500">{tt.checked_in} check-in</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          <Card><CardHeader><CardTitle className="text-sm">Detail Event</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {ev.description && <p className="text-slate-600">{ev.description}</p>}
              <div className="grid grid-cols-2 gap-2 text-slate-500">
                <div><span className="font-medium text-slate-700">Venue:</span> {ev.venue_name || '-'}</div>
                <div><span className="font-medium text-slate-700">Alamat:</span> {ev.venue_address || '-'}</div>
                <div><span className="font-medium text-slate-700">Kota:</span> {ev.venue_city || '-'}</div>
                <div><span className="font-medium text-slate-700">Tipe:</span> {ev.type}</div>
                <div><span className="font-medium text-slate-700">Mulai:</span> {formatDate(ev.start_date)}</div>
                <div><span className="font-medium text-slate-700">Selesai:</span> {formatDate(ev.end_date)}</div>
                <div><span className="font-medium text-slate-700">Organizer:</span> {ev.organizer_name || '-'}</div>
                <div><span className="font-medium text-slate-700">Kategori:</span> {ev.category || '-'}</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TICKETS TAB */}
        <TabsContent value="tickets" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-slate-800">Tipe Tiket ({(ev.ticket_types || []).length})</h3>
            <Button size="sm" onClick={() => { setTtForm(defaultTicketTypeForm); setTtEditId(null); setShowTTForm(true); }}>
              <Plus size={14} className="mr-1" /> Tambah Tipe
            </Button>
          </div>
          {(ev.ticket_types || []).length === 0 ? (
            <Card><CardContent className="text-center py-8 text-slate-400 text-sm">Belum ada tipe tiket</CardContent></Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {(ev.ticket_types || []).map(tt => (
                <Card key={tt.id} className="border-l-4" style={{ borderLeftColor: tt.color }}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge style={{ backgroundColor: tt.color + '20', color: tt.color }}>{tt.name}</Badge>
                        {!tt.is_active && <Badge variant="outline" className="text-xs">Nonaktif</Badge>}
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => {
                          setTtForm({ name: tt.name, description: tt.description || '', benefits: tt.benefits || [], price: tt.price, quota: tt.quota, sale_start: (tt.sale_start || '').slice(0, 16), sale_end: (tt.sale_end || '').slice(0, 16), section_name: tt.section_name || '', color: tt.color || '#3B82F6', sort_order: tt.sort_order || 0, is_active: tt.is_active !== false });
                          setTtEditId(tt.id); setShowTTForm(true);
                        }}><Edit size={14} /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteTT(tt.id)}><Trash2 size={14} /></Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="font-bold text-lg">{tt.price > 0 ? formatPrice(tt.price) : 'GRATIS'}</span>
                      <span className="text-slate-500">{tt.sold_count || 0}/{tt.quota || '∞'} terjual</span>
                    </div>
                    {tt.description && <p className="text-xs text-slate-500">{tt.description}</p>}
                    {(tt.benefits || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {tt.benefits.map((b, i) => <Badge key={i} variant="outline" className="text-[10px]">{b}</Badge>)}
                      </div>
                    )}
                    {tt.section_name && <p className="text-xs text-slate-400">Section: {tt.section_name}</p>}
                    {(tt.sale_start || tt.sale_end) && <p className="text-xs text-slate-400">Penjualan: {tt.sale_start ? formatDate(tt.sale_start) : '...'} - {tt.sale_end ? formatDate(tt.sale_end) : '...'}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SCHEDULE TAB */}
        <TabsContent value="schedule" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-slate-800">Jadwal/Rundown ({(ev.schedules || []).length})</h3>
            <Button size="sm" onClick={() => { setSchedForm(defaultScheduleForm); setSchedEditId(null); setShowSchedForm(true); }}>
              <Plus size={14} className="mr-1" /> Tambah Hari
            </Button>
          </div>
          {(ev.schedules || []).length === 0 ? (
            <Card><CardContent className="text-center py-8 text-slate-400 text-sm">Belum ada jadwal</CardContent></Card>
          ) : (
            <div className="space-y-4">
              {(ev.schedules || []).map(sch => (
                <Card key={sch.id}>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-sm">{sch.day_label || 'Hari'}</CardTitle>
                      <p className="text-xs text-slate-400">{sch.date || '-'}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => {
                        setSchedForm({ date: sch.date || '', day_label: sch.day_label || '', sessions: sch.sessions?.length ? sch.sessions : [{ time: '', end_time: '', title: '', description: '', speaker_name: '', location: '' }], sort_order: sch.sort_order || 0 });
                        setSchedEditId(sch.id); setShowSchedForm(true);
                      }}><Edit size={14} /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteSched(sch.id)}><Trash2 size={14} /></Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(sch.sessions || []).length === 0 ? (
                      <p className="text-xs text-slate-400">Belum ada sesi</p>
                    ) : (
                      <div className="space-y-2">
                        {sch.sessions.map((s, i) => (
                          <div key={i} className="flex gap-3 items-start p-2 rounded-lg bg-slate-50">
                            <div className="text-xs font-mono text-slate-500 min-w-[90px]">{s.time || '...'}{s.end_time ? ` - ${s.end_time}` : ''}</div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-800">{s.title || 'Untitled'}</p>
                              {s.speaker_name && <p className="text-xs text-slate-500">Speaker: {s.speaker_name}</p>}
                              {s.location && <p className="text-xs text-slate-400">Lokasi: {s.location}</p>}
                              {s.description && <p className="text-xs text-slate-400 mt-1">{s.description}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* VENUE TAB */}
        <TabsContent value="venue" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-slate-800">Venue Sections ({(ev.venue_sections || []).length})</h3>
            <Button size="sm" onClick={() => { setVsForm(defaultVenueSectionForm); setVsEditId(null); setShowVSForm(true); }}>
              <Plus size={14} className="mr-1" /> Tambah Section
            </Button>
          </div>
          {ev.venue_map_url && (
            <Card><CardContent className="p-4">
              <a href={ev.venue_map_url} target="_blank" rel="noreferrer" className="text-blue-600 text-sm underline flex items-center gap-1"><MapPin size={14} /> Buka Peta Lokasi</a>
            </CardContent></Card>
          )}
          {(ev.venue_sections || []).length === 0 ? (
            <Card><CardContent className="text-center py-8 text-slate-400 text-sm">Belum ada venue section</CardContent></Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              {(ev.venue_sections || []).map(vs => (
                <Card key={vs.id} className="border-t-4" style={{ borderTopColor: vs.color }}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-slate-800">{vs.name}</h4>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => {
                          setVsForm({ name: vs.name, capacity: vs.capacity || 0, color: vs.color || '#3B82F6', description: vs.description || '', sort_order: vs.sort_order || 0 });
                          setVsEditId(vs.id); setShowVSForm(true);
                        }}><Edit size={14} /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteVS(vs.id)}><Trash2 size={14} /></Button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500">Kapasitas: {vs.capacity || '∞'}</p>
                    {vs.description && <p className="text-xs text-slate-400 mt-1">{vs.description}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* REGISTRATIONS TAB */}
        <TabsContent value="registrations" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cari peserta..." value={searchReg} onChange={e => setSearchReg(e.target.value)} className="pl-9" />
            </div>
            <Badge variant="secondary">{registrations.length} peserta</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-slate-500">
                <th className="p-2">Nama</th><th className="p-2">Email</th><th className="p-2">Tipe Tiket</th>
                <th className="p-2">Qty</th><th className="p-2">Bayar</th><th className="p-2">Status</th><th className="p-2">Tanggal</th>
              </tr></thead>
              <tbody>
                {filteredRegs.map(r => (
                  <tr key={r.id} className="border-b hover:bg-slate-50">
                    <td className="p-2 font-medium">{r.customer_name}</td>
                    <td className="p-2 text-slate-500">{r.customer_email}</td>
                    <td className="p-2">{r.ticket_type_name}</td>
                    <td className="p-2">{r.quantity}</td>
                    <td className="p-2">{r.total_price > 0 ? formatPrice(r.total_price) : 'Gratis'}</td>
                    <td className="p-2">
                      <Badge className={
                        r.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                        r.payment_status === 'free' ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }>{r.payment_status}</Badge>
                    </td>
                    <td className="p-2 text-slate-400 text-xs">{formatDate(r.registered_at)}</td>
                  </tr>
                ))}
                {filteredRegs.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">Belum ada peserta</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* CHECK-IN TAB */}
        <TabsContent value="checkin" className="space-y-4">
          <Card><CardContent className="p-6 space-y-4">
            <h3 className="font-bold text-slate-800 flex items-center gap-2"><QrCode size={20} /> Check-In Peserta</h3>
            <p className="text-sm text-slate-500">Scan QR code dari e-ticket atau masukkan nomor tiket untuk check-in peserta.</p>

            {/* QR Scanner */}
            <div className="flex gap-2 mb-2">
              <Button
                variant={scannerActive ? 'destructive' : 'outline'}
                onClick={scannerActive ? stopScanner : startScanner}
                className="gap-2"
              >
                {scannerActive ? <CameraOff size={16} /> : <Camera size={16} />}
                {scannerActive ? 'Tutup Kamera' : 'Scan QR Code'}
              </Button>
            </div>
            <div
              id={scannerContainerId}
              className={scannerActive ? 'rounded-lg overflow-hidden border border-slate-200 bg-black mb-2' : ''}
              style={scannerActive ? { maxWidth: 400, minHeight: 300 } : { display: 'none' }}
            />

            {/* Manual input */}
            <div className="flex gap-2">
              <Input
                placeholder="Ketik nomor tiket (EVT-XXXXXXXX) atau paste QR data..."
                value={checkInInput}
                onChange={e => setCheckInInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCheckIn()}
                className="flex-1"
              />
              <Button onClick={handleCheckIn} disabled={checkInLoading}>
                {checkInLoading ? <Loader2 size={16} className="animate-spin mr-1" /> : <QrCode size={16} className="mr-1" />}
                Check-In
              </Button>
            </div>
            {checkInResult && (
              <div className={`p-4 rounded-lg ${checkInResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {checkInResult.success ? (
                  <div className="space-y-1">
                    <p className="font-bold text-green-800 flex items-center gap-2"><CheckCircle size={18} /> {checkInResult.message}</p>
                    {checkInResult.ticket && (
                      <div className="text-sm text-green-700 space-y-0.5">
                        <p>Nama: <b>{checkInResult.ticket.attendee_name}</b></p>
                        <p>Tiket: {checkInResult.ticket.ticket_type_name} — {checkInResult.ticket.ticket_number}</p>
                        {checkInResult.ticket.section_name && <p>Section: {checkInResult.ticket.section_name}</p>}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-red-700 flex items-center gap-2"><XCircle size={18} /> {checkInResult.message}</p>
                )}
              </div>
            )}
          </CardContent></Card>

          {/* Ticket list with check-in status */}
          <Card><CardHeader><CardTitle className="text-sm">Daftar Tiket ({tickets.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-slate-500">
                    <th className="p-2">No. Tiket</th><th className="p-2">Nama</th><th className="p-2">Tipe</th>
                    <th className="p-2">Section</th><th className="p-2">Status</th><th className="p-2">Aksi</th>
                  </tr></thead>
                  <tbody>
                    {tickets.map(t => (
                      <tr key={t.id} className="border-b hover:bg-slate-50">
                        <td className="p-2 font-mono text-xs">{t.ticket_number}</td>
                        <td className="p-2">{t.attendee_name}</td>
                        <td className="p-2">{t.ticket_type_name}</td>
                        <td className="p-2 text-slate-400">{t.section_name || '-'}</td>
                        <td className="p-2">
                          {t.checked_in ? (
                            <Badge className="bg-green-100 text-green-700"><CheckCircle size={12} className="mr-1" /> Checked-in</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500">Belum</Badge>
                          )}
                        </td>
                        <td className="p-2">
                          {t.checked_in ? (
                            <Button size="sm" variant="ghost" className="text-xs text-orange-500" onClick={() => handleUndoCheckIn(t.id)}>Undo</Button>
                          ) : (
                            <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setCheckInInput(t.ticket_number); }}>Check-in</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {tickets.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">Belum ada tiket</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sub-form Dialogs */}
      <TicketTypeFormDialog open={showTTForm} onOpenChange={setShowTTForm} form={ttForm} setForm={setTtForm} editId={ttEditId} saving={saving} onSave={handleSaveTT} />
      <ScheduleFormDialog open={showSchedForm} onOpenChange={setShowSchedForm} form={schedForm} setForm={setSchedForm} editId={schedEditId} saving={saving} onSave={handleSaveSched} />
      <VenueSectionFormDialog open={showVSForm} onOpenChange={setShowVSForm} form={vsForm} setForm={setVsForm} editId={vsEditId} saving={saving} onSave={handleSaveVS} />
      <EventFormDialog open={showForm} onOpenChange={setShowForm} form={form} setForm={setForm} editId={editId} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ============================================================
// Form Dialogs
// ============================================================

function EventFormDialog({ open, onOpenChange, form, setForm, editId, saving, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editId ? 'Edit Event' : 'Buat Event Baru'}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Judul Event *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <Label>Deskripsi</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} />
            </div>
            <div className="col-span-2"><Label>Thumbnail URL</Label><Input value={form.thumbnail} onChange={e => setForm(f => ({ ...f, thumbnail: e.target.value }))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="upcoming">Akan Datang</SelectItem>
                  <SelectItem value="ongoing">Berlangsung</SelectItem>
                  <SelectItem value="completed">Selesai</SelectItem>
                  <SelectItem value="cancelled">Dibatalkan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Kategori</Label><Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} /></div>
          </div>
          <div className="border-t pt-4"><p className="text-sm font-semibold text-slate-700 mb-3">📍 Venue / Lokasi</p>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nama Venue</Label><Input value={form.venue_name} onChange={e => setForm(f => ({ ...f, venue_name: e.target.value }))} /></div>
              <div><Label>Kota</Label><Input value={form.venue_city} onChange={e => setForm(f => ({ ...f, venue_city: e.target.value }))} /></div>
              <div className="col-span-2"><Label>Alamat Lengkap</Label><Input value={form.venue_address} onChange={e => setForm(f => ({ ...f, venue_address: e.target.value }))} /></div>
              <div className="col-span-2"><Label>Link Google Maps</Label><Input value={form.venue_map_url} onChange={e => setForm(f => ({ ...f, venue_map_url: e.target.value }))} /></div>
            </div>
          </div>
          <div className="border-t pt-4"><p className="text-sm font-semibold text-slate-700 mb-3">📅 Waktu</p>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tanggal Mulai</Label><Input type="datetime-local" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} /></div>
              <div><Label>Tanggal Selesai</Label><Input type="datetime-local" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} /></div>
            </div>
          </div>
          <div className="border-t pt-4"><p className="text-sm font-semibold text-slate-700 mb-3">👤 Organizer</p>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Nama</Label><Input value={form.organizer_name} onChange={e => setForm(f => ({ ...f, organizer_name: e.target.value }))} /></div>
              <div><Label>Telepon</Label><Input value={form.organizer_phone} onChange={e => setForm(f => ({ ...f, organizer_phone: e.target.value }))} /></div>
              <div><Label>Email</Label><Input value={form.organizer_email} onChange={e => setForm(f => ({ ...f, organizer_email: e.target.value }))} /></div>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t">
            <Switch checked={form.is_published} onCheckedChange={v => setForm(f => ({ ...f, is_published: v }))} />
            <Label>Publish Event (tampil di storefront)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={onSave} disabled={saving}>{saving && <Loader2 size={14} className="animate-spin mr-1" />}{editId ? 'Update' : 'Buat Event'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TicketTypeFormDialog({ open, onOpenChange, form, setForm, editId, saving, onSave }) {
  const [benefitInput, setBenefitInput] = useState('');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editId ? 'Edit Tipe Tiket' : 'Tambah Tipe Tiket'}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div><Label>Nama Tipe Tiket *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="VIP / Regular / Early Bird" /></div>
          <div><Label>Deskripsi</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Harga (0 = gratis)</Label><Input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: parseInt(e.target.value) || 0 }))} /></div>
            <div><Label>Kuota (0 = unlimited)</Label><Input type="number" value={form.quota} onChange={e => setForm(f => ({ ...f, quota: parseInt(e.target.value) || 0 }))} /></div>
          </div>
          <div>
            <Label>Benefits</Label>
            <div className="flex gap-2 mb-2">
              <Input value={benefitInput} onChange={e => setBenefitInput(e.target.value)} placeholder="Tambah benefit..."
                onKeyDown={e => { if (e.key === 'Enter' && benefitInput.trim()) { setForm(f => ({ ...f, benefits: [...f.benefits, benefitInput.trim()] })); setBenefitInput(''); e.preventDefault(); } }} />
              <Button type="button" size="sm" variant="outline" onClick={() => { if (benefitInput.trim()) { setForm(f => ({ ...f, benefits: [...f.benefits, benefitInput.trim()] })); setBenefitInput(''); } }}>+</Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {(form.benefits || []).map((b, i) => (
                <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setForm(f => ({ ...f, benefits: f.benefits.filter((_, j) => j !== i) }))}>
                  {b} <X size={10} className="ml-1" />
                </Badge>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Mulai Jual</Label><Input type="datetime-local" value={form.sale_start} onChange={e => setForm(f => ({ ...f, sale_start: e.target.value }))} /></div>
            <div><Label>Akhir Jual</Label><Input type="datetime-local" value={form.sale_end} onChange={e => setForm(f => ({ ...f, sale_end: e.target.value }))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Nama Section Venue</Label><Input value={form.section_name} onChange={e => setForm(f => ({ ...f, section_name: e.target.value }))} /></div>
            <div><Label>Warna Badge</Label><Input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} className="h-10" /></div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} />
            <Label>Aktif (bisa dibeli)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={onSave} disabled={saving}>{saving && <Loader2 size={14} className="animate-spin mr-1" />}{editId ? 'Update' : 'Tambah'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleFormDialog({ open, onOpenChange, form, setForm, editId, saving, onSave }) {
  const addSession = () => setForm(f => ({ ...f, sessions: [...f.sessions, { time: '', end_time: '', title: '', description: '', speaker_name: '', location: '' }] }));
  const removeSession = (i) => setForm(f => ({ ...f, sessions: f.sessions.filter((_, j) => j !== i) }));
  const updateSession = (i, key, val) => setForm(f => ({ ...f, sessions: f.sessions.map((s, j) => j === i ? { ...s, [key]: val } : s) }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editId ? 'Edit Jadwal' : 'Tambah Jadwal'}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Label Hari *</Label><Input value={form.day_label} onChange={e => setForm(f => ({ ...f, day_label: e.target.value }))} placeholder="Day 1 - Opening Ceremony" /></div>
            <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Sesi ({form.sessions.length})</Label>
              <Button type="button" size="sm" variant="outline" onClick={addSession}><Plus size={12} className="mr-1" /> Sesi</Button>
            </div>
            <div className="space-y-3">
              {form.sessions.map((s, i) => (
                <div key={i} className="border rounded-lg p-3 space-y-2 relative">
                  {form.sessions.length > 1 && <button onClick={() => removeSession(i)} className="absolute top-2 right-2 text-red-400 hover:text-red-600"><X size={14} /></button>}
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-[11px]">Mulai</Label><Input value={s.time} onChange={e => updateSession(i, 'time', e.target.value)} placeholder="09:00" /></div>
                    <div><Label className="text-[11px]">Selesai</Label><Input value={s.end_time} onChange={e => updateSession(i, 'end_time', e.target.value)} placeholder="10:00" /></div>
                    <div><Label className="text-[11px]">Judul *</Label><Input value={s.title} onChange={e => updateSession(i, 'title', e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[11px]">Speaker</Label><Input value={s.speaker_name} onChange={e => updateSession(i, 'speaker_name', e.target.value)} /></div>
                    <div><Label className="text-[11px]">Lokasi</Label><Input value={s.location} onChange={e => updateSession(i, 'location', e.target.value)} /></div>
                  </div>
                  <div><Label className="text-[11px]">Deskripsi</Label><Input value={s.description} onChange={e => updateSession(i, 'description', e.target.value)} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={onSave} disabled={saving}>{saving && <Loader2 size={14} className="animate-spin mr-1" />}{editId ? 'Update' : 'Tambah'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VenueSectionFormDialog({ open, onOpenChange, form, setForm, editId, saving, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editId ? 'Edit Venue Section' : 'Tambah Venue Section'}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div><Label>Nama Section *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="VIP Area / Tribune A" /></div>
          <div><Label>Kapasitas (0 = unlimited)</Label><Input type="number" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: parseInt(e.target.value) || 0 }))} /></div>
          <div><Label>Warna</Label><Input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} className="h-10" /></div>
          <div><Label>Deskripsi</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={onSave} disabled={saving}>{saving && <Loader2 size={14} className="animate-spin mr-1" />}{editId ? 'Update' : 'Tambah'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
